import * as dayjs from 'dayjs';
import { Component, ViewEncapsulation, OnInit, AfterViewInit, ViewChild } from '@angular/core';
import { first, flatten, find, cloneDeep, sortBy, maxBy, filter, some, map } from 'lodash';
import MemberSummary from 'src/app/models/memberSummary';
import OpenEnrollment from 'src/app/models/openEnrollment';
import { SubscriberService } from 'src/app/services/subscriber.service';
import Subscriber from 'src/app/models/subscriber';
import EnrollmentPeriod from 'src/app/models/enrollmentPeriod';
import { PDFComponent } from './../../../shared/components/pdf/pdf.component';
import { ActivatedRoute, Router } from '@angular/router';
import { CoreService} from 'src/app/services/core.service';
import SubscriberEffectiveSummary from 'src/app/models/subscriberEffectiveSummary';
import SubscriberContact from 'src/app/models/subscriberContact';
import { SpinnerOverlayService } from 'src/app/services/spinnerOverlay.service';
import { lastValueFrom } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import PlanType from 'src/app/models/planType';
import { env } from 'src/env/development';
import { AdminService } from 'src/app/services/admin.service';
import { EnrollmentService } from 'src/app/services/enrollment.service';

@UntilDestroy()
@Component({
  selector: 'subscriber-summary',
  templateUrl: 'subscriber.summary.component.html',
  styleUrls: [],
  encapsulation: ViewEncapsulation.None
})
export class SubscriberSummaryComponent implements OnInit {
  @ViewChild('pdf') coveragePDF: PDFComponent;
  @ViewChild('oePdf') coverageOePDF: PDFComponent;
  subscriberModel: Subscriber;
  subscriber: SubscriberEffectiveSummary;
  futureOeSubscriber: SubscriberEffectiveSummary;
  futureOeButtonLabel: string | null = null;
  subscriberContact = new SubscriberContact();
  openEnrollment: OpenEnrollment;
  electablePlanTypesForCurrentPeriod: [];
  inAdminState = false;
  isFullBenefits = false;
  medicareDEnabled = false;
  showOEButton = false;
  documentTitleText = 'STATEMENT OF INSURANCE';
  documentHeaderText =
    `THIS STATEMENT SUMMARIZES YOUR INSURANCE COVERAGES WITH THE HEALTH CARE AUTHORITY. IF THIS STATEMENT ` +
    `DISAGREES WITH YOUR RECORDS, PLEASE CONTACT YOUR EMPLOYER'S PERSONNEL, PAYROLL, OR BENEFITS OFFICE (IF YOU ` +
    `ARE AN EMPLOYEE) OR PEBB BENEFITS SERVICES AT 1-800-200-1004 (IF YOU ARE A RETIREE, COBRA, OR LEAVE ` +
    `WITHOUT PAY SUBSCRIBER).`;
  documentFooterText =
    `THIS STATEMENT IS NOT A GUARANTEE OF INSURANCE. IT IS INTENEDED TO BE A STATEMENT OF RECORD OF YOUR ` +
    `ENROLLMENT. THE INSURANCE COVERAGE IS GOVERNED BY THE INSURANCE CONTRACT OR CERTIFICATE OF COVERAGE.`;
  enrolledHeader = 'ENROLLMENT INFORMATION';
  
  constructor(
    private subscriberService: SubscriberService,
    private route: ActivatedRoute,
    private router: Router,
    private coreService: CoreService,
    private spinnerService: SpinnerOverlayService,
    private adminService: AdminService,
    private enrollmentService: EnrollmentService
    ) {}

  async ngOnInit(): Promise<void> {
    this.medicareDEnabled = await lastValueFrom(this.adminService.getFeatureFlag('FEATURE_ENABLE_MEDICARE_D'));

    if (this.router.url.includes('admin')) {
      this.inAdminState = true;
    }

    const data = this.route.snapshot.data;

    this.spinnerService.show();
    this.subscriberService.getSubscriberById(data.subscriber.memberId).pipe(untilDestroyed(this))
      .subscribe(async (subscriber) =>
        {
          this.subscriberModel = subscriber;

          this.isFullBenefits = subscriber.organization?.benefitSetup?.benefitSetupCode === 'FB';
          this.subscriberContact.memberId = subscriber.memberId;
          this.subscriberContact.emailNotificationInd = subscriber.emailNotificationInd;
          this.subscriberContact.emailAddress = subscriber.emailAddress;
          this.subscriberContact.homePhoneNumber = subscriber.homePhoneNumber;
          this.subscriberContact.workPhoneNumber = subscriber.workPhoneNumber;
          this.subscriberContact.cellPhoneNumber = subscriber.cellPhoneNumber;

          this.subscriber = await this.getSubscriberSummary(subscriber, null);

          const futureOeEnrollmentPeriod = first(subscriber.enrollmentPeriods.filter(ep => ep.isOpenEnrollment && ep.coverageEffectiveStartDate > new Date()));
          const futureOeCoverageDate = futureOeEnrollmentPeriod?.coverageEffectiveStartDate;
          if (futureOeCoverageDate) {
            this.futureOeButtonLabel = dayjs(futureOeCoverageDate).format('MMMM D, YYYY');
            const allEnrollments = flatten(subscriber.members.map(m => m.enrollments));
            this.showOEButton = some(allEnrollments, e => e.effectiveStartDate.getTime() === futureOeCoverageDate.getTime());

            this.futureOeSubscriber = await this.getSubscriberSummary(subscriber, futureOeEnrollmentPeriod);
          }

          this.spinnerService.hide();
        });
  }

  async getSubscriberSummary(subscriber: Subscriber, enrollmentPeriod: EnrollmentPeriod | null): Promise<SubscriberEffectiveSummary> {
    let subscriberSummary  = new SubscriberEffectiveSummary(subscriber, enrollmentPeriod, enrollmentPeriod === null, undefined, undefined, false, this.medicareDEnabled);
    subscriberSummary.memberSummary = subscriberSummary.memberSummary.filter(ms => ms.dentalEffectiveDates || ms.medicalEffectiveDates);
    subscriberSummary.memberSummary = sortBy(subscriberSummary.memberSummary, (m: MemberSummary) => m.sortOrder);
    subscriberSummary.memberSummary = subscriberSummary.memberSummary.filter((ms) => ms.isVerified);

    //get electable coverage types for the current period (medical, dental and vision only)
    var planTypeCodesToInclude = [env.medicalPlanTypeCode, env.dentalPlanTypeCode, env.visionPlanTypeCode];
    this.electablePlanTypesForCurrentPeriod = map(filter(subscriberSummary.currentlyEffectiveEnrollmentPeriod.electablePlanTypes,
      (o:PlanType)=> some(planTypeCodesToInclude, (pt) => pt == o.planTypeCode)),'planTypeName');

    // US28495 - Show rates for retirees.  Edit the if block above if we want COBRA or LWOP (rates are loaded into DB)
    if (['R', 'K'].indexOf(subscriberSummary.memberType.memberTypeCode) > -1) {
      const spRates = await lastValueFrom(this.enrollmentService.getMemberSelfPayAvailablePlans(false, subscriberSummary.memberId, new Date(), true));
      const medicalSpPlan = find(spRates, spr => spr.planId === subscriberSummary.accountEffectiveMedical?.planId);
      const dentalSpPlan = find(spRates, spr => spr.planId === subscriberSummary.accountEffectiveDental?.planId);
      const visionSpPlan = find(spRates, spr => spr.planId === subscriberSummary.accountEffectiveVision?.planId);

      if (medicalSpPlan && medicalSpPlan.rates?.length > 0) {
        subscriberSummary.medicalPremium = medicalSpPlan.rates[0].rateAmt;
        subscriberSummary.totalCost += subscriberSummary.medicalPremium;
      }

      if (dentalSpPlan && dentalSpPlan.rates?.length > 0) {
        subscriberSummary.dentalPremium = dentalSpPlan.rates[0].rateAmt;
        subscriberSummary.totalCost += subscriberSummary.dentalPremium;
      }

      if (visionSpPlan && visionSpPlan.rates?.length > 0) {
        subscriberSummary.visionPremium = visionSpPlan.rates[0].rateAmt;
        subscriberSummary.totalCost += subscriberSummary.visionPremium;
      }
    }

    return subscriberSummary;
  }

  exportStatementOfInsurance(): void {
    this.coveragePDF.pdfElement.saveAs('StatementOfInsurance.pdf');
  }

  exportOEStatementOfInsurance(): void {
    this.coverageOePDF.pdfElement.saveAs('StatementOfInsurance.pdf');
  }

  async updateEmailIndicator(): Promise<void> {
    this.spinnerService.show();
    try {
      this.subscriberContact = await lastValueFrom(this.subscriberService.updateSubscriberContact(this.subscriberContact));
      this.subscriberModel.emailNotificationInd = this.subscriberContact.emailNotificationInd;
      this.subscriber.emailNotificationsInd = this.subscriberContact.emailNotificationInd;
      this.coreService.setSubscriber(this.subscriberModel);
      this.spinnerService.hide();
      this.coreService.popMessage('You have successfully updated your contact information!', 'success', 5000);
    } catch (err) {
      this.spinnerService.hide();
      this.coreService.popMessage('Something went wrong updating your contact information! Please try again', 'error', 5000);
      console.log(err);
    }
  }

  navToCoverage(e): void {
     e.preventDefault();
  }
}
